import type { BookingRequest, BookingRequestState, WaitingCount } from "@dentacore/shared-types";

import { api } from "./client";

/**
 * The approval queue — **branch-scoped**, like the appointments it decides on.
 *
 * Nothing writes into this queue yet (P3 Q5): the WhatsApp agent is Phase 6,
 * and the approval rule is built and tested first so that phase adds a writer
 * rather than reopening the reschedule path. Until then the panel is empty on a
 * real clinic, which is the honest state rather than a gap.
 */

export function listBookingRequests(
  params: { state?: BookingRequestState; kind?: "RESCHEDULE" | "NEW" } = {},
  signal?: AbortSignal,
): Promise<BookingRequest[]> {
  const query = new URLSearchParams();
  if (params.state !== undefined) query.set("state", params.state);
  if (params.kind !== undefined) query.set("kind", params.kind);

  const suffix = query.toString();
  return api<BookingRequest[]>(`/booking-requests${suffix === "" ? "" : `?${suffix}`}`, {
    branchScoped: true,
    signal,
  });
}

/**
 * Approve a reschedule — the server re-checks, then moves.
 *
 * **A refusal comes back as a 409 carrying a sentence and a `reason`**, and the
 * request stays pending: the slot may have gone since the patient asked, and
 * staff need to offer them another time rather than have the request closed on
 * their behalf.
 */
export function approveBookingRequest(requestId: string): Promise<BookingRequest> {
  return api<BookingRequest>(`/booking-requests/${requestId}/approve`, {
    method: "POST",
    branchScoped: true,
  });
}

/** Decline — records who and when, and moves nothing. */
export function declineBookingRequest(requestId: string): Promise<BookingRequest> {
  return api<BookingRequest>(`/booking-requests/${requestId}/decline`, {
    method: "POST",
    branchScoped: true,
  });
}

/**
 * How many things are waiting on a human in this branch — the header chip.
 *
 * **One number over both queues** (HC1), branch-scoped like everything else
 * (HC4). Read on mount, after any decision, and when the tab regains focus;
 * **nothing polls** — P3 Q9 deferred live updates until the hosting decision,
 * and this story is not the place to introduce the first timer in the app.
 */
export function readWaitingCount(signal?: AbortSignal): Promise<WaitingCount> {
  return api<WaitingCount>("/booking-requests/waiting", { branchScoped: true, signal });
}
